import { useState, useMemo } from 'react';
import {
  ChevronRight,
  ChevronDown,
  ExpandAll,
  CollapseAll,
  WarningHex,
} from '@carbon/react/icons';
import classNames from 'classnames';
import { ConveyanceEdge, OwnershipNode } from './types';
import { getEdgeTypeLabel } from './data-helpers';
import { FloatingTooltip } from 'components/floating-tooltip';
import {
  buildEdgeRows,
  EntityLink,
  OwnershipDetailTable,
} from './conveyance-runsheet';
import styles from './conveyance-runsheet.module.scss';

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

interface TransactionParty {
  name: string;
  entityId: number;
}

export interface TransactionData {
  transactionId: number;
  edges: ConveyanceEdge[];
  effectiveDate: string;
  conveyors: TransactionParty[];
  conveyees: TransactionParty[];
  conveyedEstates: string[];
}

interface TransactionSectionProps {
  txn: TransactionData;
  nodeMap: Record<string, OwnershipNode>;
  documentId: number;
  selectedTransactionId?: number | null;
  onEdgeClick?: (edgeId: string, documentId: number) => void;
  onTransactionClick?: (transactionId: number | null) => void;
  defaultOpsExpanded?: boolean;
}

function countLogicalOps(edges: ConveyanceEdge[]): number {
  const rows = buildEdgeRows(edges);
  let count = 0;
  for (const row of rows) {
    if (row.kind === 'paired') count += 2;
    else if (row.kind === 'consolidation') count += row.edges.length;
    else count += 1;
  }
  return count;
}

// * Extracted transaction section — renders identically to the runsheet's
// expanded document > transaction view. Reuses the same CSS module.
// @returns The rendered transaction section JSX element.
export function TransactionSection({
  txn,
  nodeMap,
  documentId,
  selectedTransactionId,
  onEdgeClick,
  onTransactionClick,
  defaultOpsExpanded = true,
}: TransactionSectionProps) {
  const [opsExpanded, setOpsExpanded] = useState(defaultOpsExpanded);
  const [expandedEdgeIds, setExpandedEdgeIds] = useState<Set<string>>(
    new Set()
  );
  const txnOps = useMemo(() => countLogicalOps(txn.edges), [txn.edges]);
  const edgeRows = useMemo(() => buildEdgeRows(txn.edges), [txn.edges]);

  const toggleEdge = (id: string) => {
    setExpandedEdgeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAllEdges = () => {
    setExpandedEdgeIds((prev) => {
      const next = new Set(prev);
      for (const row of edgeRows) {
        if (row.kind === 'single') next.add(row.edge.id);
        else if (row.kind === 'paired') next.add(row.groupKey);
        else if (row.kind === 'consolidation') next.add(row.groupKey);
      }
      return next;
    });
  };

  const collapseAllEdges = () => setExpandedEdgeIds(new Set());

  // ── Edge row renderer (matches runsheet's renderEdgeRows) ──────────────

  function renderEdgeRows() {
    return edgeRows.map((row) => {
      if (row.kind === 'single') {
        const { edge } = row;
        const fromNode = edge.fromNodeId ? nodeMap[edge.fromNodeId] : undefined;
        const toNode = edge.toNodeId ? nodeMap[edge.toNodeId] : undefined;
        const hasExpandable =
          edge.fromNodeId !== null && edge.toNodeId !== null;

        return (
          <div key={edge.id}>
            <div
              className={styles.edgeRow}
              onClick={() => onEdgeClick?.(edge.id, documentId)}
            >
              <span className={`${styles.edgeCell} ${styles.edgeCellFixed}`}>
                {hasExpandable && (
                  <button
                    type="button"
                    className={styles.edgeExpandToggle}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleEdge(edge.id);
                    }}
                  >
                    {expandedEdgeIds.has(edge.id) ? (
                      <ChevronDown />
                    ) : (
                      <ChevronRight />
                    )}
                  </button>
                )}
                {getEdgeTypeLabel(edge.eventType, undefined, fromNode, toNode)}
                {!edge.isValid && (
                  <span className={styles.invalidBadge}>Invalid</span>
                )}
              </span>
              <span className={`${styles.edgeCell} ${styles.edgeCellFlex}`}>
                {fromNode && (
                  <EntityLink entityId={fromNode.entity.entityId}>
                    {fromNode.entity.entityName}
                  </EntityLink>
                )}
              </span>
              <span className={`${styles.edgeCell} ${styles.edgeCellFlex}`}>
                {toNode && (
                  <EntityLink entityId={toNode.entity.entityId}>
                    {toNode.entity.entityName}
                  </EntityLink>
                )}
              </span>
              <span className={`${styles.edgeCell} ${styles.edgeCellFlex}`}>
                {!edge.isValid && edge.validationReason ? (
                  <span>
                    <WarningHex />
                    {edge.validationReason}
                  </span>
                ) : (
                  <span>
                    {getEdgeTypeLabel(
                      edge.eventType,
                      edge.params.retained,
                      fromNode,
                      toNode
                    )}
                  </span>
                )}
              </span>
            </div>
            {expandedEdgeIds.has(edge.id) && fromNode && toNode && (
              <div className={styles.consolidationDetail}>
                <OwnershipDetailTable
                  preNodes={[{ node: fromNode, label: '' }]}
                  postNodes={[
                    {
                      node: toNode,
                      label: edge.params?.retained ? 'RETAINED' : '',
                    },
                  ]}
                  selectedNodeId={null}
                  onNavigate={() => {}}
                />
              </div>
            )}
          </div>
        );
      }

      if (row.kind === 'paired') {
        const { conveyedEdge, retainedEdge, groupKey } = row;
        const fromNode = conveyedEdge.fromNodeId
          ? nodeMap[conveyedEdge.fromNodeId]
          : undefined;
        const conveyedTo = conveyedEdge.toNodeId
          ? nodeMap[conveyedEdge.toNodeId]
          : undefined;
        const retainedTo = retainedEdge.toNodeId
          ? nodeMap[retainedEdge.toNodeId]
          : undefined;
        const isPairExpanded = expandedEdgeIds.has(groupKey);

        return (
          <div key={groupKey}>
            <div
              className={styles.edgeRow}
              onClick={() => onEdgeClick?.(conveyedEdge.id, documentId)}
            >
              <span className={`${styles.edgeCell} ${styles.edgeCellFixed}`}>
                <button
                  type="button"
                  className={styles.edgeExpandToggle}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleEdge(groupKey);
                  }}
                >
                  {isPairExpanded ? <ChevronDown /> : <ChevronRight />}
                </button>
                {getEdgeTypeLabel(
                  conveyedEdge.eventType,
                  undefined,
                  fromNode,
                  conveyedTo
                )}
                {!conveyedEdge.isValid && (
                  <span className={styles.invalidBadge}>Invalid</span>
                )}
              </span>
              <span className={`${styles.edgeCell} ${styles.edgeCellFlex}`}>
                {fromNode && (
                  <EntityLink entityId={fromNode.entity.entityId}>
                    {fromNode.entity.entityName}
                  </EntityLink>
                )}
              </span>
              <span className={`${styles.edgeCell} ${styles.edgeCellFlex}`}>
                {conveyedTo && (
                  <EntityLink entityId={conveyedTo.entity.entityId}>
                    {conveyedTo.entity.entityName}
                  </EntityLink>
                )}
              </span>
              <span className={`${styles.edgeCell} ${styles.edgeCellFlex}`}>
                <span>
                  {getEdgeTypeLabel(
                    conveyedEdge.eventType,
                    false,
                    fromNode,
                    conveyedTo
                  )}
                </span>
              </span>
            </div>
            {isPairExpanded && fromNode && conveyedTo && retainedTo && (
              <div className={styles.consolidationDetail}>
                <OwnershipDetailTable
                  preNodes={[{ node: fromNode, label: '' }]}
                  postNodes={[
                    { node: conveyedTo, label: '' },
                    { node: retainedTo, label: 'RETAINED' },
                  ]}
                  selectedNodeId={null}
                  onNavigate={() => {}}
                />
              </div>
            )}
          </div>
        );
      }

      if (row.kind === 'consolidation') {
        const toNode = row.edges[0]?.toNodeId
          ? nodeMap[row.edges[0].toNodeId!]
          : undefined;
        const isExpanded = expandedEdgeIds.has(row.groupKey);
        return (
          <div key={row.groupKey}>
            <div
              className={styles.edgeRow}
              onClick={() => onEdgeClick?.(row.edges[0].id, documentId)}
            >
              <span className={`${styles.edgeCell} ${styles.edgeCellFixed}`}>
                <button
                  type="button"
                  className={styles.edgeExpandToggle}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleEdge(row.groupKey);
                  }}
                >
                  {isExpanded ? <ChevronDown /> : <ChevronRight />}
                </button>
                Consolidation
              </span>
              <span className={`${styles.edgeCell} ${styles.edgeCellFlex}`}>
                {row.edges.length} sources
              </span>
              <span className={`${styles.edgeCell} ${styles.edgeCellFlex}`}>
                {toNode && (
                  <EntityLink entityId={toNode.entity.entityId}>
                    {toNode.entity.entityName}
                  </EntityLink>
                )}
              </span>
              <span className={`${styles.edgeCell} ${styles.edgeCellFlex}`}>
                Consolidation
              </span>
            </div>
            {isExpanded &&
              row.edges.map((edge) => {
                const fromNode = edge.fromNodeId
                  ? nodeMap[edge.fromNodeId]
                  : undefined;
                return (
                  <div key={edge.id} className={styles.edgeRow}>
                    <span
                      className={`${styles.edgeCell} ${styles.edgeCellFixed}`}
                    >
                      Consolidation
                    </span>
                    <span
                      className={`${styles.edgeCell} ${styles.edgeCellFlex}`}
                    >
                      {fromNode && (
                        <EntityLink entityId={fromNode.entity.entityId}>
                          {fromNode.entity.entityName}
                        </EntityLink>
                      )}
                    </span>
                    <span
                      className={`${styles.edgeCell} ${styles.edgeCellFlex}`}
                    >
                      {toNode && (
                        <EntityLink entityId={toNode.entity.entityId}>
                          {toNode.entity.entityName}
                        </EntityLink>
                      )}
                    </span>
                    <span
                      className={`${styles.edgeCell} ${styles.edgeCellFlex}`}
                    />
                  </div>
                );
              })}
          </div>
        );
      }
      return null;
    });
  }

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div
      className={classNames(styles.transactionSection, {
        [styles.selectedTransaction]:
          selectedTransactionId === txn.transactionId,
      })}
      onClick={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest(`.${styles.edgeRow}`)) return;
        onTransactionClick?.(
          selectedTransactionId === txn.transactionId ? null : txn.transactionId
        );
      }}
    >
      {/* Transaction header — matches runsheet exactly */}
      <div className={styles.txSectionHeader}>
        <div className={styles.txHeaderContent}>
          <div className={styles.txTopRow}>
            <span className={styles.txLabel}>
              Transaction #{txn.transactionId}
            </span>
            <span className={styles.txSep}>&middot;</span>
            {txn.effectiveDate && (
              <span className={styles.txMeta}>
                {formatDate(txn.effectiveDate)}
              </span>
            )}
            <span className={styles.txSep}>&middot;</span>
            <span className={styles.txMeta}>
              {txnOps} operation{txnOps !== 1 ? 's' : ''}
              {txn.conveyedEstates.length > 0 &&
                ` · ${txn.conveyedEstates.join(', ')}`}
            </span>
          </div>
          <div className={styles.txBodyColumns}>
            {txn.conveyors.length > 0 && (
              <div className={styles.txPartyLine}>
                <span className={styles.partyRoleLabel}>CONVEYORS</span>
                <span className={styles.partyNames}>
                  {txn.conveyors.map((p, i) => (
                    <span key={p.entityId}>
                      <EntityLink entityId={p.entityId}>{p.name}</EntityLink>
                      {i < txn.conveyors.length - 1 && ', '}
                    </span>
                  ))}
                </span>
              </div>
            )}
            {txn.conveyees.length > 0 && (
              <div className={styles.txPartyLine}>
                <span className={styles.partyRoleLabel}>CONVEYEES</span>
                <span className={styles.partyNames}>
                  {txn.conveyees.map((p, i) => (
                    <span key={p.entityId}>
                      <EntityLink entityId={p.entityId}>{p.name}</EntityLink>
                      {i < txn.conveyees.length - 1 && ', '}
                    </span>
                  ))}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Operations — collapsible, matching runsheet */}
      <div className={styles.opsCollapsible}>
        <div className={styles.opsCollapsibleToggle}>
          <button
            type="button"
            className={styles.opsCollapsibleToggleBtn}
            onClick={(e) => {
              e.stopPropagation();
              setOpsExpanded((prev) => !prev);
            }}
          >
            {opsExpanded ? <ChevronDown /> : <ChevronRight />}
            <span className={styles.opsCollapsibleLabel}>OPERATIONS</span>
            <span className={styles.opsCollapsibleMeta}>
              {txn.edges.length}{' '}
              {txn.edges.length === 1 ? 'operation' : 'operations'}
            </span>
          </button>
          <div className={styles.opsCollapsibleActions}>
            <FloatingTooltip content="Expand all" placement="left">
              <button
                type="button"
                className={styles.opsActionBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  expandAllEdges();
                }}
              >
                <ExpandAll />
              </button>
            </FloatingTooltip>
            <FloatingTooltip content="Collapse all" placement="left">
              <button
                type="button"
                className={styles.opsActionBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  collapseAllEdges();
                }}
              >
                <CollapseAll />
              </button>
            </FloatingTooltip>
          </div>
        </div>
        {opsExpanded && (
          <div className={styles.txSectionBody}>
            <div className={styles.edgesHeader}>
              <span className={`${styles.headerCell} ${styles.edgeCellFixed}`}>
                Operation
              </span>
              <span className={`${styles.headerCell} ${styles.edgeCellFlex}`}>
                Conveyor
              </span>
              <span className={`${styles.headerCell} ${styles.edgeCellFlex}`}>
                Conveyee
              </span>
              <span className={`${styles.headerCell} ${styles.edgeCellFlex}`}>
                Detail
              </span>
            </div>
            {renderEdgeRows()}
          </div>
        )}
      </div>
    </div>
  );
}
